import React, { useState, useRef, useEffect } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { useCitySearch } from "../../hooks/useCitySearch";

const LocationSearch = ({
  label,
  value,
  onChange,
  error,
  placeholder,
  disabled,
  className = "",
  inputClassName = "",
  hideIcon = false,
  disableCreate = false,
  clearOnSelect = false,
  includeInactive = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (value && typeof value === "object") {
      setSearchTerm(value.name || "");
    } else if (typeof value === "string") {
      setSearchTerm(value);
    } else {
      setSearchTerm("");
    }
  }, [value]);

  const { cities, loading, error: searchError } = useCitySearch(searchTerm, 300, includeInactive);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        // Revert searchTerm to the last saved value if clicked outside without selecting/creating
        if (value && typeof value === "object") {
          setSearchTerm(value.name || "");
        } else if (typeof value === "string") {
          setSearchTerm(value);
        } else {
          setSearchTerm("");
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value]);

  return (
    <div className={`flex-1 relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
          {label}
        </label>
      )}
      <div className="relative flex items-center w-full">
        {!hideIcon && (
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-900" />
        )}
        <input
          type="text"
          value={searchTerm}
          disabled={disabled}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
            if (!e.target.value) {
              onChange({ id: null, name: "" });
            }
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder || "Select City"}
          className={
            inputClassName ||
            `w-full pl-9 pr-10 h-[36px] border border-gray-200 rounded-lg text-xs sm:text-sm text-gray-900 placeholder:text-gray-400 transition-all outline-none focus:outline-none focus:border-[#FF4C18] bg-white ${
              error || searchError
                ? "border-red-500 bg-red-50"
                : "border-gray-200"
            } ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`
          }
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
        )}
      </div>
      
      {isOpen && searchTerm.trim() !== "" && cities.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {cities.map((city) => (
            <button
              key={city._id}
              type="button"
              onClick={() => {
                if (clearOnSelect) {
                  setSearchTerm("");
                } else {
                  setSearchTerm(city.name);
                }
                setIsOpen(false);
                onChange({ id: city._id, name: city.name });
              }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-orange-50 transition-colors"
            >
              {city.name}{city.stateCode ? `, ${city.stateCode}` : ""}
            </button>
          ))}
          {!disableCreate && searchTerm.trim() && !cities.some(c => c.name.toLowerCase() === searchTerm.trim().toLowerCase()) && (
            <div className="border-t border-gray-100 p-2 bg-gray-50/50 flex items-center justify-between gap-2">
              <span className="text-xs text-gray-500 truncate">
                Can't find your city?
              </span>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  const term = searchTerm.trim();
                  if (clearOnSelect) {
                    setSearchTerm("");
                  }
                  onChange(term);
                  setIsOpen(false);
                }}
                className="px-4 py-1.5 bg-[#FF4C18] text-white text-[11px] font-semibold rounded-lg hover:bg-orange-600 transition-colors shrink-0"
                style={{ fontFamily: "CabinetGrotesk-Bold" }}
              >
                Create "{searchTerm}"
              </button>
            </div>
          )}
        </div>
      )}

      {isOpen && searchTerm.trim() !== "" && !loading && cities.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl py-4 text-center px-4 flex flex-col items-center justify-center gap-2">
          <p className="text-sm text-gray-500">
            No results for <span className="font-semibold text-[#FF4C18]">"{searchTerm}"</span>
          </p>
          {!disableCreate && (
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                const term = searchTerm.trim();
                if (clearOnSelect) {
                  setSearchTerm("");
                }
                onChange(term);
                setIsOpen(false);
              }}
              className="px-6 py-1.5 bg-[#FF4C18] text-white text-xs font-semibold rounded-lg hover:bg-orange-600 transition-colors"
              style={{ fontFamily: "CabinetGrotesk-Bold" }}
            >
              Create
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationSearch;
